"use client";

import { useEffect } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { NO_INSTAGRAM_SENTINEL } from "../types/instagram";

/**
 * Keeps the form's selected-Instagram field in sync with the live
 * Page→IG bond state:
 *
 * - When no bond exists, writes `NO_INSTAGRAM_SENTINEL` so the launcher
 *   publishes Facebook-only instead of silently falling back to a
 *   random BM-owned account through `resolveInstagramIdentity`'s
 *   legacy single-BM-IG path.
 * - When a bond DOES exist and the field still holds a stale sentinel
 *   (written during an earlier load where `capabilities` hadn't
 *   resolved yet), clears it back to `undefined` so Auto picks up the
 *   real Page-linked IG at launch. Without this, defaults poisoned by
 *   a prior race keep stripping IG even though the picker now displays
 *   the handle.
 */
export function useInstagramSentinelEffect({
  form,
  fieldPath,
  enabled,
  isLoading,
  pageLinkedIg,
  instagramSource,
}: {
  form: UseFormReturn<FieldValues>;
  /** Form path of the selected-Instagram field. */
  fieldPath: string;
  /** Top-level gate — usually `!!accountId && !!pageId`. */
  enabled: boolean;
  /** Whether the upstream queries (IG accounts AND account capabilities)
   *  are still loading. Must include every query that contributes to
   *  `pageLinkedIg` or `instagramSource` — otherwise the predicate
   *  runs against a half-loaded view and writes the sentinel while a
   *  page-direct bond is still in flight. */
  isLoading: boolean;
  /** Truthy when the BM-owned list contains a page-linked IG. */
  pageLinkedIg: object | null | undefined;
  /** Strict Page→IG bond source from `getMetaPageInstagramStatus` /
   *  `getAccountCapabilities`. Either "page-direct" or
   *  "business-manager" when a bond exists. */
  instagramSource: "page-direct" | "business-manager" | null | undefined;
}) {
  useEffect(() => {
    if (!enabled || isLoading) return;
    const hasAnyBond = !!pageLinkedIg || !!instagramSource;
    const current = form.getValues(fieldPath) as string | undefined;
    if (hasAnyBond) {
      if (current === NO_INSTAGRAM_SENTINEL) {
        form.setValue(fieldPath, undefined, { shouldDirty: true });
      }
      return;
    }
    if (current === NO_INSTAGRAM_SENTINEL) return;
    form.setValue(fieldPath, NO_INSTAGRAM_SENTINEL, { shouldDirty: true });
  }, [enabled, isLoading, pageLinkedIg, instagramSource, form, fieldPath]);
}
